import React from 'react'
import { useEffect, useRef, useState } from 'react'
import { LngLat } from 'mapbox-gl'

import * as d3 from 'd3'
import {
  useFilterStore,
  useMapStore,
  usePointStore,
  usePolygonStore,
} from '@monopolio-ui/state-library'
import { EPropertyType } from '@monopolio-ui/types-library'
import { presenter } from '@wiggot/core-apis/src/presenter'
import { abbreviateNumber } from '../../utils/formatterUtils'

let moveTimeout: NodeJS.Timeout

const OffMarketPins = (): JSX.Element => {
  const { map, isLoadedMap, drawingTool, setHoveredLayer } = useMapStore()

  const { pointsSvgContainer, setHoveredOffMarketPoint } = usePointStore()

  const { polygonTiles } = usePolygonStore()

  const { propertyType } = useFilterStore()

  const pointsSvgContainerRef =
    useRef<typeof pointsSvgContainer>(pointsSvgContainer)
  pointsSvgContainerRef.current = pointsSvgContainer

  const mapRef = useRef<typeof map>(map)
  mapRef.current = map

  const propertyTypeRef = useRef<typeof propertyType>(propertyType)
  propertyTypeRef.current = propertyType

  const drawingToolRef = useRef<typeof drawingTool>(drawingTool)
  drawingToolRef.current = drawingTool

  const [sources, setSources] = useState<
    {
      source: string
      layer: string
    }[]
  >([])

  function project(dValue: number[]) {
    if (!dValue[0] || !dValue[1]) {
      return undefined
    }
    return mapRef.current?.project(new LngLat(dValue[0], dValue[1]))
  }

  function render() {
    const groupId = 'off-market-points'

    if (
      !pointsSvgContainerRef.current ||
      pointsSvgContainerRef.current.select(`#${groupId}`).size() < 1
    ) {
      return
    }

    pointsSvgContainerRef.current
      .select(`#${groupId}`)
      .selectAll('g.offMarketPoint')
      .attr('transform', (dValue: { geometry: { coordinates: number[] } }) => {
        const coordinates = project(dValue.geometry.coordinates)
        if (!coordinates) {
          return undefined
        }
        return `translate(${coordinates?.x},${coordinates?.y})`
      })
  }

  const toCenter = (params: { lat: number; lng: number }): void => {
    const { lat, lng } = params

    map?.flyTo({
      center: [lng, lat],
      essential: true,
      speed: 0.7,
      curve: 1.3,
      duration: 300,
    })
  }

  /**
   * This function is used to load the off market pins
   * @param params The parameters of the function
   * @returns {void}
   */
  const loadOffMarketPins = (params: { layersName: string[] }): void => {
    const { layersName } = params

    if (
      !mapRef.current ||
      !isLoadedMap ||
      layersName.length < 1 ||
      !pointsSvgContainerRef?.current
    ) {
      return
    }

    const features = mapRef.current.queryRenderedFeatures(
      [
        [0, 0],
        [mapRef.current.getCanvas().width, mapRef.current.getCanvas().height]
      ],
      {
        layers: layersName,
      }
    )

    if (features.length < 1) {
      return
    }

    const deletedDuplicateFeatures = features.reduce(
      (acc: any[], current: any) => {
        const find = acc.find(
          (item: { properties: { id: any } }) =>
            item.properties.id === current?.properties?.id
        )

        if (!find) {
          return acc.concat([
            {
              ...current,
              properties: {
                ...current?.properties,
                layerId: current.layer.id,
                sourceId: current.source,
                sourceLayer: current.sourceLayer,
                featureId: current.id,
              },
            },
          ])
        }
        return acc
      },
      []
    )

    createPointInstance({
      features: deletedDuplicateFeatures,
    })
  }

  const createPointInstance = (params: { features: any[] }) => {
    const { features } = params

    const groupId = 'off-market-points'

    if (!pointsSvgContainerRef.current || features.length < 1) {
      return
    }

    if (pointsSvgContainerRef.current.select(`#${groupId}`).size() > 0) {
      pointsSvgContainerRef.current.select(`#${groupId}`).remove()
    }

    const group = pointsSvgContainerRef.current.append('g').attr('id', groupId)

    const points = group
      .selectAll('g.offMarketPoint')
      .data(
        features.map((dValue) => {
          const { lng, lat } = JSON.parse(dValue?.properties?.centroid)
          return {
            geometry: {
              coordinates: [lng, lat],
            },
            properties: dValue?.properties,
          }
        })
      )
      .enter()
      .append('g')
      .attr('class', 'offMarketPoint')
      .attr('id', (dValue: any) => {
        const { properties } = dValue
        return properties?.id
      })
      .on(
        'mouseover',
        (dEvent: { currentTarget: any }, dData: { properties: any }) => {
          if (
            drawingToolRef.current.isActive &&
            drawingToolRef.current.status !== 'processed'
          ) {
            return
          }
          setHoveredLayer({
            layerId: 'offMarketPoint',
            properties: dData?.properties,
          })

          setHoveredOffMarketPoint(dData?.properties)

          pointsSvgContainerRef?.current?.style('cursor', 'pointer')

          const point = d3.select(dEvent.currentTarget)
          point.raise()

          point.select('rect').style('fill', '#484A5E')
        }
      )
      .on('mouseout', (dEvent: { currentTarget: any }) => {
        setHoveredOffMarketPoint(undefined)

        if (
          drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed'
        ) {
          return
        }

        setHoveredLayer(undefined)

        pointsSvgContainerRef?.current?.style('cursor', 'grab')

        const point = d3.select(dEvent.currentTarget)
        point.select('rect').style('fill', '#898CA8')
      })
      .on('click', (_dEvent: any, dValue: { properties: any }) => {
        const { properties } = dValue
        const nomenclature = presenter<{
          id: string
          centroid: string
        }>({
          value: properties,
          style: 'camel',
        })
        const { lat, lng } = JSON.parse(nomenclature.centroid)
        // setOffMarketCoords({ lat, lng })
        // setPropertyId(nomenclature.id)
        toCenter({ lat, lng })
      })

    group.lower()

    points
      .append('rect')
      .attr(
        'width',
        (dValue: {
          properties: {
            rent_avg: number
            price_avg: number
          }
        }) => {
          const { properties } = dValue
          const value =
            propertyTypeRef.current === EPropertyType.RENT
              ? properties?.rent_avg || 0
              : properties?.price_avg || 0

          if (value < 1) {
            return 90
          }

          const abbreviate = abbreviateNumber({
            value,
          })

          const abbreviateNumberLength = abbreviate.length
          const variant = 0.9 * abbreviateNumberLength
          if (abbreviateNumberLength < 3) {
            return 54
          }
          return 54 + variant
        }
      )
      .attr('height', 24)
      .style('fill', '#898CA8')
      .attr('rx', 14)
      .attr('ry', 14)
      .attr('x', -25)
      .attr('y', -7)
      .attr('class', 'offMarketPoint')

    const iconTextGroup = points.append('g')

    iconTextGroup
      .append('text')
      .attr('y', -3)
      .attr(
        'x',
        (dValue: {
          properties: {
            rent_avg: number
            price_avg: number
          }
        }) => {
          const { properties } = dValue
          const value =
            propertyTypeRef.current === EPropertyType.RENT
              ? properties?.rent_avg || 0
              : properties?.price_avg || 0

          if (value < 1) {
            return 20
          }

          const abbreviate = abbreviateNumber({
            value,
          })

          const abbreviateNumberLength = abbreviate.length
          const variant = 0.8 * abbreviateNumberLength

          return variant
        }
      )
      .attr('text-anchor', 'middle')
      .attr('dy', '12px')
      .attr('font-family', 'Ret Hat Display')
      .attr('font-weight', '700')
      .attr('fill', '#ffffff')
      .attr('class', 'offMarketPoint')
      .text((dValue: any) => {
        const { properties } = dValue

        const value =
          propertyTypeRef.current === EPropertyType.RENT
            ? properties?.rent_avg || 0
            : properties?.price_avg || 0

        if (value < 1) {
          return 'Sin estimado'
        }

        const formattedPrice = abbreviateNumber({
          value,
        })
        return `$${formattedPrice}`
      })

    render()
  }

  /**
   * This function is used to create the loader events for the off market pins
   * @param source The source of the layer
   * @param layerName The name of the layer
   * @returns {void}
   */
  const createLoaderEvents = (params: {
    sources: string[]
    layers: string[]
  }): void => {
    if (!map || !isLoadedMap) {
      return
    }

    const { sources, layers } = params

    map.on('sourcedata', (e) => {
      if (sources.includes(e?.sourceId ?? '')) {
        const zoom = mapRef?.current?.getZoom()

        if (zoom && zoom < 18) {
          removeAll()
          return
        }

        loadOffMarketPins({
          layersName: layers,
        })
      }
    })

    mapRef.current?.on('move', () => {
      const zoom = mapRef?.current?.getZoom()

      if (zoom && zoom < 18) {
        removeAll()
        return
      }

      render()
    })

    map.on('moveend', () => {
      const zoom = mapRef?.current?.getZoom()

      if (zoom && zoom < 18) {
        removeAll()
        return
      }

      clearTimeout(moveTimeout)
      moveTimeout = setTimeout(() => {
        loadOffMarketPins({
          layersName: layers,
        })
      }, 500)

      render()
    })
  }

  const removeAll = (): void => {
    const groupId = 'off-market-points'
    if (
      pointsSvgContainerRef.current &&
      pointsSvgContainerRef.current.select(`#${groupId}`).size() > 0
    ) {
      setHoveredOffMarketPoint(undefined)
      pointsSvgContainerRef.current.select(`#${groupId}`).remove()
    }
  }

  useEffect(() => {
    if (!sources || sources.length < 1 || !map || !isLoadedMap) {
      return
    }

    createLoaderEvents({
      sources: sources.map((dValue) => dValue.source),
      layers: sources.map((dValue) => dValue.layer),
    })
  }, [sources])

  useEffect(() => {
    if (polygonTiles?.length < 1) {
      return
    }

    loadOffMarketPins({
      layersName: sources.map((dValue) => dValue.layer),
    })
  }, [pointsSvgContainer])

  useEffect(() => {
    if (!map || !isLoadedMap || polygonTiles?.length < 1) {
      return
    }

    const entities = polygonTiles.reduce(
      (
        acc: {
          source: string
          layer: string
        }[],
        current
      ) => {
        const {
          tiles: { properties },
        } = current

        if (!properties?.id) {
          return acc
        }

        const { id } = properties

        const source = `${id}-property-source`
        const layer = `${id}-property-polygon`

        return acc.concat([
          {
            source,
            layer,
          },
        ])
      },
      []
    )

    if (entities.length < 1) {
      return
    }

    setSources(entities)
  }, [map, isLoadedMap, polygonTiles])
  return <></>
}

export default OffMarketPins
